'use client';

import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import type Experiments from '@alltrails/core/types/Experiments';
// eslint-disable-next-line @alltrails/custom/react-imports
import React, { Fragment, PropsWithChildren } from 'react';
import { createIntl } from '@formatjs/intl';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import type { ListsState } from '@alltrails/lists/slices/lists';
import setGlobalFetchHeaders from '@alltrails/core/utils/setGlobalFetchHeaders';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Bugsnag from '@bugsnag/js';
import type { MapState } from '@alltrails/maps/redux/reducer';
import type { userAgent } from 'next/server';
import { buildStore } from '../utils/redux';
import { setupListeners } from '@/utils/listenerMiddleware';

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  lists: ListsState;
  map: MapState;
  messages: ReturnType<typeof createIntl>['messages'];
  locale: ReturnType<typeof createIntl>['locale'];
  irclickid?: string;
};

setupListeners();
const plugin = Bugsnag.getPlugin('react');
const ErrorBoundary = plugin ? plugin.createErrorBoundary(React) : Fragment;

export default function CustomProvider({
  bootstrap,
  browser,
  experiments,
  lists,
  map,
  messages,
  locale,
  children,
  irclickid
}: PropsWithChildren<Props>) {
  const languageRegionCode = useLanguageRegionCode();
  setGlobalFetchHeaders({ languageRegionCode });
  const store = buildStore({
    algoliaConfigs: bootstrap.algoliaConfig,
    amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
    browser,
    context: bootstrap.context,
    currencyCode: bootstrap.currencyCode,
    experiments,
    features: bootstrap.features,
    irclickid,
    lists,
    map,
    plans: bootstrap.plans,
    requestCountryCode: bootstrap.requestCountryCode
  });

  return (
    <ErrorBoundary>
      <IntlProvider messages={messages} locale={locale}>
        <Provider store={store}>{children}</Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
}
